import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, GitMerge, X, Undo2 } from "lucide-react";

/** Duplicate Review Tab — surfaces MEDIUM-confidence dedup candidates
 * from `/api/admin/dedup/queue` for merge / reject decisions, plus a
 * one-shot "Backfill last 7 days" trigger.
 *
 * See `dedup_service.py` for how items land in this queue:
 *  - HIGH  → auto-merged during ingestion, never shown here
 *  - MED   → queued here for a human decision
 *  - LOW   → ignored
 */
export const DedupReviewTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [status, setStatus] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dedup/queue", { params: { status } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load dedup queue");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const merge = async (canonical_id, candidate_id) => {
    try {
      await api.post("/admin/dedup/queue/merge", { canonical_id, candidate_id });
      toast.success("Merged");
      load();
    } catch (e) { toast.error("Merge failed"); }
  };

  const reject = async (canonical_id, candidate_id) => {
    try {
      await api.post("/admin/dedup/queue/reject", { canonical_id, candidate_id });
      toast.success("Rejected");
      load();
    } catch (e) { toast.error("Reject failed"); }
  };

  const backfill = async () => {
    if (!window.confirm("Run dedup backfill over the last 7 days? Runs in background — check queue in ~5 min.")) return;
    setBackfilling(true);
    try {
      await api.post("/admin/dedup/backfill?days=7");
      toast.success("Backfill started — check back in a few minutes");
    } catch (e) {
      toast.error("Backfill failed to start");
    } finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-4" data-testid="dedup-review-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A1A1A]">Duplicate Review</h2>
          <p className="text-sm text-[#6B7280]">
            Uncertain matches — same event, possibly different sources. Auto-merges
            (HIGH confidence) never appear here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-sm"
            data-testid="dedup-status-filter"
          >
            <option value="pending">Pending</option>
            <option value="merged">Merged</option>
            <option value="rejected">Rejected</option>
          </select>
          <button
            onClick={load}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
            data-testid="dedup-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={backfill}
            disabled={backfilling}
            className="px-3 py-1.5 bg-[#0AADA0] text-white rounded text-sm hover:bg-[#089489] disabled:opacity-50"
            data-testid="dedup-backfill-btn"
          >
            {backfilling ? "Starting..." : "Backfill 7d"}
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="p-8 text-center text-[#6B7280] border border-dashed border-[#E5E7EB] rounded">
          No {status} items.
        </div>
      )}

      <div className="space-y-3">
        {items.map((row) => (
          <DedupCard
            key={row.id}
            row={row}
            onMerge={() => merge(row.canonical.id, row.candidate.id)}
            onReject={() => reject(row.canonical.id, row.candidate.id)}
            readOnly={row.status !== "pending"}
          />
        ))}
      </div>
    </div>
  );
};

const DedupCard = ({ row, onMerge, onReject, readOnly }) => {
  const scoreColor = row.cosine >= 0.85 ? "#0AADA0"
                    : row.cosine >= 0.72 ? "#F59E0B" : "#EF4444";
  return (
    <div
      className="border border-[#E5E7EB] rounded-lg p-4 bg-white"
      data-testid={`dedup-card-${row.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4 text-xs">
          <span className="uppercase tracking-wider text-[#6B7280]">
            {row.island}
          </span>
          <span
            className="px-2 py-0.5 rounded font-mono"
            style={{ background: `${scoreColor}20`, color: scoreColor }}
          >
            cosine {row.cosine?.toFixed(3)}
          </span>
          <span className="px-2 py-0.5 rounded font-mono bg-[#F3F4F6] text-[#4B5563]">
            pHash Δ {row.phash_distance}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <button
                onClick={onMerge}
                className="px-3 py-1.5 bg-[#0AADA0] text-white rounded text-xs inline-flex items-center gap-1 hover:bg-[#089489]"
                data-testid="dedup-merge-btn"
              >
                <GitMerge size={12} /> Merge
              </button>
              <button
                onClick={onReject}
                className="px-3 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-[#F9FAFB]"
                data-testid="dedup-reject-btn"
              >
                <X size={12} /> Reject
              </button>
            </>
          )}
          {readOnly && (
            <span className="text-xs uppercase tracking-wider text-[#6B7280]">
              {row.status}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ArticlePreview label="Canonical (keep)" a={row.canonical} />
        <ArticlePreview label="Candidate (merge into canonical)" a={row.candidate} />
      </div>
    </div>
  );
};

const ArticlePreview = ({ label, a }) => (
  <div className="border border-[#F3F4F6] rounded p-3 bg-[#FAFAFA]">
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
      {label} · {a.source_name}
    </div>
    <div className="flex gap-3">
      {a.image_url && (
        <img
          src={a.image_url}
          alt=""
          className="w-24 h-24 object-cover rounded flex-shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1A1A1A] line-clamp-2 mb-1">
          {a.title}
        </div>
        <div className="text-xs text-[#6B7280] line-clamp-3">
          {a.ai_summary}
        </div>
        <div className="text-[10px] text-[#9CA3AF] mt-1">
          {a.category} · {a.published_at?.slice(0, 10)}
        </div>
      </div>
    </div>
  </div>
);
